import { cancelableUploadBase64ImageTask } from '@modules/image-helpers';
import { logger } from '@modules/logger';
import { cloneDeep } from 'es-toolkit';
// es-toolkit/compat: lodash forEach iterates objects/Records (not in es-toolkit main); lodash isEmpty treats 0/true/null as empty
import { forEach, isEmpty } from 'es-toolkit/compat';
import { EventEmitter } from 'eventemitter3';

import { EmailSignatureFieldTypes } from '../consts';
import type { EmailSignatureFieldIdTypes, EmailSignatureV2 } from '../types';

type OriginalImageFieldIdTypes = `ORIGINAL-${EmailSignatureFieldIdTypes}`;

class EmailSignatureImagesUploader extends EventEmitter {
  uploadedImagesUrls: Record<EmailSignatureFieldIdTypes | OriginalImageFieldIdTypes, string>;
  cancelablePendingUploadTasks: Record<
    EmailSignatureFieldIdTypes | OriginalImageFieldIdTypes,
    (() => void) | undefined
  >;

  constructor() {
    super();
    this.uploadedImagesUrls = {};
    this.cancelablePendingUploadTasks = {};
    this._startListeningToImageUploadFinished();
  }

  clearEverything(): void {
    Object.keys(this.cancelablePendingUploadTasks).forEach((key) => {
      delete this.cancelablePendingUploadTasks[key as EmailSignatureFieldIdTypes];
    });

    Object.keys(this.uploadedImagesUrls).forEach((key) => {
      delete this.uploadedImagesUrls[key as EmailSignatureFieldIdTypes];
    });
  }

  _getFolderPath(
    emailSignatureFieldType: EmailSignatureFieldTypes,
    isOriginalImage: boolean,
  ): string {
    if (isOriginalImage) {
      return 'email_signatures_images/original_images';
    } else {
      if (
        emailSignatureFieldType === EmailSignatureFieldTypes.BANNER ||
        emailSignatureFieldType === EmailSignatureFieldTypes.IMAGE
      ) {
        return 'email_signatures_images';
      } else {
        return 'email_signatures_icons';
      }
    }
  }

  _isBase64OrLocalBlob(imageUrl: string): boolean {
    return imageUrl.startsWith('data:image') || imageUrl.startsWith('blob:');
  }

  async uploadImage(
    fieldIdTheImageBelongsTo: EmailSignatureFieldIdTypes,
    localImageUrl: string,
    fieldType: EmailSignatureFieldTypes,
    onErrorUploading: (fieldType: EmailSignatureFieldTypes) => void,
    isOriginalImage = false,
  ): Promise<void> {
    const theActualFieldIdTheImageBelongsTo:
      | EmailSignatureFieldIdTypes
      | OriginalImageFieldIdTypes = isOriginalImage
      ? `ORIGINAL-${fieldIdTheImageBelongsTo}`
      : fieldIdTheImageBelongsTo;
    this.uploadedImagesUrls[theActualFieldIdTheImageBelongsTo] = '';

    const cancelPreviouslyUnfinishedUploadTask =
      this.cancelablePendingUploadTasks[theActualFieldIdTheImageBelongsTo];
    if (cancelPreviouslyUnfinishedUploadTask) {
      cancelPreviouslyUnfinishedUploadTask();
    }

    const cancelableUploadTask = cancelableUploadBase64ImageTask(
      localImageUrl,
      {
        onSuccess: (iconDBImageUrl) => {
          delete this.cancelablePendingUploadTasks[theActualFieldIdTheImageBelongsTo];
          this.uploadedImagesUrls[theActualFieldIdTheImageBelongsTo] = iconDBImageUrl;
          this.emit('UPLOAD_IMAGE_FINISHED');
        },
        onError: (error) => {
          if (error.code !== 'storage/canceled') {
            onErrorUploading(fieldType);
          }
        },
      },
      this._getFolderPath(fieldType, isOriginalImage),
      // Deliberately no image id: every upload lands on a fresh object name so an
      // existing object can never be repointed at different content. The storage
      // rules deny `update` on these prefixes to enforce that.
    );
    this.cancelablePendingUploadTasks[theActualFieldIdTheImageBelongsTo] = cancelableUploadTask;
  }

  _startListeningToImageUploadFinished(): void {
    this.on('UPLOAD_IMAGE_FINISHED', () => {
      if (isEmpty(this.cancelablePendingUploadTasks)) {
        this.emit('EVERY_IMAGE_HAS_FINISHED_UPLOADING');
      }
    });
  }

  finishUpAllUploadingJobs(): Promise<void> {
    // If every image has finished uploading, resolve immediately, else
    // hooking into event emitter and resolve when everything is ready
    return new Promise((resolve) => {
      if (isEmpty(this.cancelablePendingUploadTasks)) {
        resolve();
      } else {
        this.on('EVERY_IMAGE_HAS_FINISHED_UPLOADING', resolve);
      }
    });
  }

  replaceLocalImagesUrlsWithStorageImagesUrls(localEmailSignature: EmailSignatureV2): {
    emailSignatureWithStorageImagesUrls: EmailSignatureV2;
  } {
    const emailSignatureWithStorageImagesUrls = cloneDeep(localEmailSignature);

    forEach(emailSignatureWithStorageImagesUrls.contactDetails, (contactDetailsField) => {
      // no upload job for this field, i.e its icon image has not changed
      const currentIconUrl =
        emailSignatureWithStorageImagesUrls.contactDetails[contactDetailsField.id].iconUrl;
      const isBase64Image = this._isBase64OrLocalBlob(currentIconUrl);
      if (!this.uploadedImagesUrls[contactDetailsField.id] && !isBase64Image) return;
      if (!this.uploadedImagesUrls[contactDetailsField.id] && isBase64Image) {
        logger.error('Error uploading icon images for contact icons');
        throw new Error('Error uploading icon images');
      }

      emailSignatureWithStorageImagesUrls.contactDetails[contactDetailsField.id].iconUrl =
        this.uploadedImagesUrls[contactDetailsField.id];
    });

    forEach(emailSignatureWithStorageImagesUrls.socialLinks, (socialLink) => {
      // no upload job for this social link, i.e its icon image has not changed
      const currentIconUrl = emailSignatureWithStorageImagesUrls.socialLinks[socialLink.id].iconUrl;
      const isBase64Image = this._isBase64OrLocalBlob(currentIconUrl);

      if (!this.uploadedImagesUrls[socialLink.id] && !isBase64Image) return;
      if (!this.uploadedImagesUrls[socialLink.id] && isBase64Image) {
        logger.error('Error uploading icon images for social icons');
        throw new Error('Error uploading social icon images');
      }

      emailSignatureWithStorageImagesUrls.socialLinks[socialLink.id].iconUrl =
        this.uploadedImagesUrls[socialLink.id];
    });

    forEach(emailSignatureWithStorageImagesUrls.images, (imageField, index) => {
      // no upload job for this image, i.e its image has not changed
      if (!imageField.imageUrl) return;

      const currentImageUrl = imageField.imageUrl;
      const isBase64Image = this._isBase64OrLocalBlob(currentImageUrl);
      if (!this.uploadedImagesUrls[imageField.id] && !isBase64Image) return;
      if (!this.uploadedImagesUrls[imageField.id] && isBase64Image) {
        logger.error('Error uploading images');
        throw new Error('Error uploading images');
      }

      emailSignatureWithStorageImagesUrls.images[index].imageUrl =
        this.uploadedImagesUrls[imageField.id];

      const idOfTheOriginalImage: OriginalImageFieldIdTypes = `ORIGINAL-${imageField.id}`;
      const currentOriginalImageUrl =
        emailSignatureWithStorageImagesUrls.images[index].originalImageUrl;
      const isBase64OriginalImage = this._isBase64OrLocalBlob(currentOriginalImageUrl);
      if (!this.uploadedImagesUrls[idOfTheOriginalImage] && !isBase64OriginalImage) return;
      if (!this.uploadedImagesUrls[idOfTheOriginalImage] && isBase64OriginalImage) {
        logger.error('Error uploading images');
        throw new Error('Error uploading images');
      }

      emailSignatureWithStorageImagesUrls.images[index].originalImageUrl =
        this.uploadedImagesUrls[idOfTheOriginalImage];
    });

    if (
      emailSignatureWithStorageImagesUrls.environmentalFooter?.iconUrl &&
      this.uploadedImagesUrls[emailSignatureWithStorageImagesUrls.environmentalFooter.id]
    ) {
      const environmentalFooterId = emailSignatureWithStorageImagesUrls.environmentalFooter.id;
      emailSignatureWithStorageImagesUrls.environmentalFooter.iconUrl =
        this.uploadedImagesUrls[environmentalFooterId];
    }

    if (
      emailSignatureWithStorageImagesUrls.environmentalFooter?.iconUrl &&
      this._isBase64OrLocalBlob(emailSignatureWithStorageImagesUrls.environmentalFooter?.iconUrl) &&
      !this.uploadedImagesUrls[emailSignatureWithStorageImagesUrls.environmentalFooter.id]
    ) {
      logger.error('Error uploading environmental icon image');
      throw new Error('Error uploading environmental icon image');
    }

    if (
      emailSignatureWithStorageImagesUrls.banner?.imageUrl &&
      this.uploadedImagesUrls[emailSignatureWithStorageImagesUrls.banner.id]
    ) {
      const bannerId = emailSignatureWithStorageImagesUrls.banner.id;
      emailSignatureWithStorageImagesUrls.banner.imageUrl = this.uploadedImagesUrls[bannerId];

      const idOfTheOriginalBannerImage: OriginalImageFieldIdTypes = `ORIGINAL-${emailSignatureWithStorageImagesUrls.banner.id}`;
      if (this.uploadedImagesUrls[idOfTheOriginalBannerImage]) {
        emailSignatureWithStorageImagesUrls.banner.originalImageUrl =
          this.uploadedImagesUrls[idOfTheOriginalBannerImage];
      }
    }

    if (
      emailSignatureWithStorageImagesUrls.banner?.imageUrl &&
      this._isBase64OrLocalBlob(emailSignatureWithStorageImagesUrls.banner?.imageUrl) &&
      !this.uploadedImagesUrls[emailSignatureWithStorageImagesUrls.banner.id]
    ) {
      logger.error('Error uploading banner image');
      throw new Error('Error uploading banner image');
    }
    return { emailSignatureWithStorageImagesUrls };
  }
}

export default EmailSignatureImagesUploader;
